import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import ConfirmationModal from "../../components/ConfirmationModal";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const EQUIPMENT_TYPES = ["PC", "Notebook", "Servidor", "Impressora", "Switch", "Roteador", "Monitor", "Telefone", "Outro"];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  paper: { overflowX: "auto" },
  table: { minWidth: 600 },
  formField: { marginBottom: theme.spacing(2) },
}));

const emptyForm = { name: "", type: "", serialNumber: "", location: "", active: true };

const Equipamentos = () => {
  const classes = useStyles();
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEquipments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment");
      setEquipments(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEquipments();
  }, [fetchEquipments]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (eq) => {
    setEditing(eq);
    setForm({ name: eq.name, type: eq.type || "", serialNumber: eq.serialNumber || "", location: eq.location || "", active: eq.active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/equipment/${editing.id}`, form);
        toast.success("Equipamento atualizado");
      } else {
        await api.post("/equipment", form);
        toast.success("Equipamento criado");
      }
      setDialogOpen(false);
      fetchEquipments();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/equipment/${deleteTarget.id}`);
      toast.success("Equipamento excluído");
      setDeleteTarget(null);
      fetchEquipments();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Equipamentos (CMDB)</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo equipamento
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>N° de série</TableCell>
                <TableCell>Localização</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Carregando...</TableCell>
                </TableRow>
              ) : equipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">Nenhum equipamento cadastrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : equipments.map((eq) => (
                <TableRow key={eq.id} hover>
                  <TableCell><strong>{eq.name}</strong></TableCell>
                  <TableCell>
                    {eq.type && (
                      <Chip label={eq.type} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{eq.serialNumber || "—"}</TableCell>
                  <TableCell>{eq.location || "—"}</TableCell>
                  <TableCell>
                    <Switch
                      checked={eq.active}
                      size="small"
                      color="primary"
                      onChange={async () => {
                        await api.put(`/equipment/${eq.id}`, { ...eq, active: !eq.active });
                        fetchEquipments();
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(eq)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="secondary" onClick={() => setDeleteTarget(eq)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar equipamento" : "Novo equipamento"}</DialogTitle>
        <DialogContent>
          <TextField
            className={classes.formField}
            label="Nome *"
            fullWidth
            variant="outlined"
            size="small"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            className={classes.formField}
            label="Tipo"
            fullWidth
            variant="outlined"
            size="small"
            select
            SelectProps={{ native: true }}
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            <option value="">Selecionar...</option>
            {EQUIPMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>
          <TextField
            className={classes.formField}
            label="N° de série"
            fullWidth
            variant="outlined"
            size="small"
            value={form.serialNumber}
            onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
          />
          <TextField
            className={classes.formField}
            label="Localização"
            fullWidth
            variant="outlined"
            size="small"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={!form.name}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir equipamento"
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      >
        Deseja excluir o equipamento <strong>{deleteTarget?.name}</strong>? Chamados vinculados perderão a referência.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Equipamentos;
